<!-- eslint-disable vue/no-v-html -->
<!-- eslint-disable vue/no-v-text-v-html-on-component -->
<template>
  <!--
    v-html it's globally disallowed, so for cases where we have no
    choise (eg: the BE returning raw html from Froala).
    We use only here as the raw html props it's passed through
    a sanitized as a layer of protection

    Tag statici invece di <component :is="tag"> dinamico: il compilatore SSR
    di Vue ottimizza v-html solo su elementi con tag noto a compile-time. Su
    un componente dinamico il binding v-html finisce su un percorso diverso
    che in SSR non legge mai il valore reattivo — nessun errore, contenuto
    semplicemente vuoto nell'HTML servito (bug isolato con log mirati: il
    computed non veniva proprio invocato durante il render server). tag è
    sempre uno tra 'div' (default) / 'span' / 'p' in tutto il codebase.
  -->
  <p v-if="tag === 'p'" v-bind="$attrs" v-html="tailwindBasedSafeHtml" />
  <span v-else-if="tag === 'span'" v-bind="$attrs" v-html="tailwindBasedSafeHtml" />
  <div v-else v-bind="$attrs" v-html="tailwindBasedSafeHtml" />
</template>

<script setup lang="ts">
import sanitizeHtml from 'sanitize-html'
import { computed } from 'vue'

const allowedTags = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'a',
  'abbr',
  'acronym',
  'b',
  'blockquote',
  'br',
  'code',
  'div',
  'hr',
  'em',
  'i',
  'li',
  'ol',
  'p',
  'span',
  'strong',
  'table',
  'thead',
  'tbody',
  'th',
  'td',
  'tr',
  'ul',
  'img',
]

const allowedAttributes = {
  '*': [
    'align',
    'alink',
    'alt',
    'bgcolor',
    'border',
    'cellpadding',
    'cellspacing',
    'class',
    'color',
    'cols',
    'colspan',
    'coords',
    'dir',
    'face',
    'height',
    'href',
    'hspace',
    'ismap',
    'lang',
    'marginheight',
    'marginwidth',
    'multiple',
    'nohref',
    'noresize',
    'noshade',
    'nowrap',
    'ref',
    'rel',
    'rev',
    'rows',
    'rowspan',
    'scope',
    'scrolling',
    'shape',
    'span',
    'summary',
    'tabindex',
    'title',
    'usemap',
    'valign',
    'value',
    'vlink',
    'vspace',
    'width',
  ],
  a: ['href', 'alt', 'title', 'rel'],
  img: ['src'],
}

const props = withDefaults(defineProps<{
  rawHtml: string,
  tag?: string,
  shiftHeadings?: boolean,
  unwrapParagraphs?: boolean,
  linkClass?: string,
  linkifyPhoneNumbers?: boolean,
}>(), { tag: 'div' })

const { convertToTailwindClasses } = useRichTextToTailwind()

// Su /privacy-e-cookie-policy un editor ha incollato nel rich text del CMS un
// documento HTML completo copiato da un generatore esterno
// (<!DOCTYPE html><html><head><style>...</style></head><body>...</body></html>),
// non solo il frammento della tabella. Affidarsi al solo sanitize-html per
// rimuovere il <style> non è bastato (l'errore persisteva anche dopo aver
// aggiunto nonTextTags), quindi qui rimuoviamo esplicitamente l'"involucro" da
// documento HTML — con una regex nostra, verificabile, che non dipende dal
// comportamento interno di una libreria terza — prima ancora di sanitizzare.
const stripPastedDocumentChrome = (html: string): string =>
  html
    .replace(/<!DOCTYPE[^>]*>/gi, '')
    .replace(/<head\b[^>]*>[\s\S]*?<\/head>/gi, '')
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<\/?(html|body)\b[^>]*>/gi, '')

const safeHtml = computed(() =>
  sanitizeHtml(stripPastedDocumentChrome(props.rawHtml), {
    allowedTags,
    allowedAttributes,
    // Difesa in profondità: se un <style> comparisse comunque fuori da un
    // <head> (non questo caso, ma in generale), non deve sopravvivere qui.
    nonTextTags: ['script', 'style', 'textarea', 'option'],
  }),
)

// WAVE "Skipped heading level" (WCAG 1.3.1) is a SEQUENTIAL check: a heading
// may only be at most 1 level deeper than the PREVIOUS heading in reading
// order, regardless of what other levels appear elsewhere in the same
// content. A set-based "compact the distinct levels used" approach (tried
// previously) fails on documents like h2, h2, h4, h3, h2: h3 exists
// somewhere in the content, so the level *set* {2,3,4} looks gap-free even
// though the h4 still directly follows an h2 in reading order — the actual
// violation WAVE flags. This walks the headings in document order instead,
// tracking the previous (possibly already-clamped) level and clamping each
// new one to at most previousLevel + 1, exactly mirroring the check itself.
const shiftHeadingLevels = (html: string): string => {
  let previousLevel = 1 // l'h1 di pagina, fuori da questo contenuto
  let lastMappedLevel = previousLevel

  return html.replace(/<(\/?)(h[1-6])(\b[^>]*)>/gi, (_, slash, tag, attrs) => {
    if (slash) {
      return `</h${lastMappedLevel}${attrs}>`
    }

    const originalLevel = parseInt(tag[1])
    const newLevel = Math.min(originalLevel, previousLevel + 1)
    previousLevel = newLevel
    lastMappedLevel = newLevel
    return `<h${newLevel}${attrs}>`
  })
}

// Rimuove le intestazioni completamente vuote (es. <h3><span class="font-bold">
// </span></h3>, un <strong></strong> vuoto lasciato nel rich text del CMS):
// un heading senza alcun testo è sempre un errore, indipendentemente dalla
// pagina, quindi non serve un prop dedicato — si applica sempre (WAVE "Empty
// heading", WCAG 2.4.6)
const removeEmptyHeadings = (html: string): string =>
  html.replace(/<(h[1-6])\b[^>]*>([\s\S]*?)<\/\1>/gi, (match, _tag, inner) => {
    const textOnly = inner.replace(/<[^>]*>/g, '').trim()
    return textOnly ? match : ''
  })

// Rimuove i tag <p> del rich text del CMS quando il contenuto è già avvolto
// in un heading/contenitore proprio (es. titolo/sottotitolo hero): un <p> corto
// che eredita una dimensione carattere grande viene segnalato da WAVE come
// "Possible heading" anche senza classi proprie (WCAG 1.3.1)
const unwrapParagraphTags = (html: string): string =>
  html.replace(/<\/?p(\b[^>]*)?>/gi, '')

// Trasforma numeri di telefono italiani (fissi 0XXXXXXXXX o mobili 3XXXXXXXX)
// scritti come testo semplice in link cliccabili tel:. Non è attivo di default
// — solo dove esplicitamente richiesto (es. footer?.contacts) — per non
// rischiare falsi positivi su altre sequenze numeriche (es. P.IVA) in campi
// di testo diversi.
const linkifyPhoneNumbers = (html: string): string =>
  html.replace(/\b(0\d{8,10}|3\d{8,9})\b/g, (match) =>
    `<a href="tel:+39${match}" class="hover:underline">${match}</a>`,
  )

const tailwindBasedSafeHtml = computed(() => {
  let result = props.linkClass
    ? convertToTailwindClasses(safeHtml.value, props.linkClass)
    : convertToTailwindClasses(safeHtml.value)
  result = removeEmptyHeadings(result)
  if (props.shiftHeadings) result = shiftHeadingLevels(result)
  if (props.unwrapParagraphs) result = unwrapParagraphTags(result)
  if (props.linkifyPhoneNumbers) result = linkifyPhoneNumbers(result)
  return result
})
</script>
